import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Licenses from './pages/Licenses';
import LicenseDetail from './pages/LicenseDetail';
import Users from './pages/Users';
import History from './pages/History';
import Settings from './pages/Settings';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Backups from './pages/Backups';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, requiredPermission = null }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission) {
    const [module, action] = requiredPermission.split('.');
    const perms = user?.permissions || {};
    if (!perms[module]?.[action]) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <Layout>{children}</Layout>;
};

// Public Route - redirect to dashboard if authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />

      <Route path="/licenses" element={
        <ProtectedRoute requiredPermission="licenses.view">
          <Licenses />
        </ProtectedRoute>
      } />

      <Route path="/licenses/new" element={<Navigate to="/licenses" replace />} />

      <Route path="/licenses/:id" element={
        <ProtectedRoute requiredPermission="licenses.view">
          <LicenseDetail />
        </ProtectedRoute>
      } />

      <Route path="/licenses/:id/edit" element={<Navigate to="/licenses" replace />} />

      <Route path="/history" element={
        <ProtectedRoute requiredPermission="history.view">
          <History />
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute requiredPermission="users.manage">
          <Users />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute requiredPermission="settings.manage">
          <Settings />
        </ProtectedRoute>
      } />

      <Route path="/tickets" element={
        <ProtectedRoute requiredPermission="tickets.view">
          <Tickets />
        </ProtectedRoute>
      } />

      <Route path="/tickets/:id" element={
        <ProtectedRoute requiredPermission="tickets.view">
          <TicketDetail />
        </ProtectedRoute>
      } />

      <Route path="/backups" element={
        <ProtectedRoute requiredPermission="settings.manage">
          <Backups />
        </ProtectedRoute>
      } />

      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RealtimeProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: 'rgb(15 23 42)',
                  color: 'rgb(241 245 249)',
                  border: '1px solid rgb(30 41 59)',
                },
              }}
            />
          </RealtimeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
